import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DashboardLayout } from '@/components/DashboardLayout';
import { getPatientRequests, respondToPatientRequest } from '@/services/api';
import { toast } from 'sonner';
import { ClipboardCheck } from 'lucide-react';

export default function PatientRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      const data = await getPatientRequests();
      setRequests(data);
    } catch (error) {
      toast.error('Unable to load requests');
    } finally {
      setLoading(false);
    }
  };

  const handleDecision = async (requestId, status) => {
    try {
      await respondToPatientRequest(requestId, status);
      toast.success(`Request ${status}`);
      loadRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to update request');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="patient-requests-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Provider Access Requests
          </h1>
          <p className="text-slate-500">Approve or deny provider requests for your health records.</p>
        </div>

        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-blue-900" />Incoming Requests
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="skeleton h-24 rounded-lg" />
            ) : requests.length === 0 ? (
              <p className="text-sm text-slate-500" data-testid="patient-requests-empty">No requests yet.</p>
            ) : (
              <div className="space-y-3">
                {requests.map((request) => (
                  <div key={request.id} className="border border-slate-200 rounded-lg p-4" data-testid={`patient-request-${request.id}`}>
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{request.provider_name || 'Provider'} ({request.patient_identifier})</p>
                        <p className="text-xs text-slate-500">Reason: {request.reason}</p>
                        {request.requested_record_types?.length > 0 && (
                          <p className="text-xs text-slate-500 mt-1">Types: {request.requested_record_types.join(', ')}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" data-testid={`patient-request-status-${request.id}`}>
                          {request.status}
                        </Badge>
                        {request.status === 'pending' && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              className="bg-blue-900 hover:bg-blue-800 text-white"
                              onClick={() => handleDecision(request.id, 'approved')}
                              data-testid={`patient-request-approve-${request.id}`}
                            >
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDecision(request.id, 'denied')}
                              data-testid={`patient-request-deny-${request.id}`}
                            >
                              Deny
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}